/**
 * What a picker knows about the rest of the list.
 *
 * @remarks
 * A picker asks for one page and offers no way to browse, so this is
 * not about fetching the next one. It is about telling the filler the truth:
 * whether what he sees is everything, and if not, how much there is.
 *
 * @packageDocumentation
 */

/** The page a source returned, as the picker needs to read it. */
export interface Page {
  /** The continuation, unchanged, or absent when there is none. */
  readonly cursor: string | undefined;
  /** Whether rows exist beyond this page. */
  readonly hasMore: boolean;
  /** How many rows are in this page. */
  readonly shown: number;
  /** How many rows there are in total. */
  readonly total: number;
}

/**
 * Reads the page information off an answer.
 *
 * @remarks
 * Completeness comes from `count` against `totalRecords` and NOT from whether a
 * cursor exists. Measured: Resource Graph gives no cursor exactly when it
 * reports having truncated, so a client that infers "there is more" from "the
 * cursor exists" reports complete on precisely the case where the answer was
 * cut off. The picker guide explains why under "Filtering belongs at the
 * source".
 *
 * @param answer - The envelope `queryResources` returns.
 * @returns What the picker may say about the rest of the list.
 */
export function pageOf(answer: {
  $skipToken?: string;
  count: number;
  totalRecords: number;
}): Page {
  return {
    cursor: answer.$skipToken,
    hasMore: answer.count < answer.totalRecords,
    shown: answer.count,
    total: answer.totalRecords,
  };
}

/** What a `select` hands a picker: the rows, and what is known about the rest. */
export interface Selected<TItem> {
  /** How many rows of the answer the picker could not offer, because they lack a field it guarantees. */
  readonly droppedRowCount: number;
  readonly items: readonly TItem[];
  readonly page: Page;
}

/** An answer as `queryResources` returns it, narrowed to what a `select` reads. */
export interface Answer {
  readonly $skipToken?: string;
  readonly count: number;
  readonly data: readonly unknown[];
  readonly totalRecords: number;
}

/** A row keeps its Resource Graph type; an absent or empty one becomes the literal `unknown`. */
function fillMissingType(row: unknown): unknown {
  if (typeof row !== 'object' || row === null) return row;
  const candidate: Record<string, unknown> = { ...row };
  const type = candidate.type;
  return {
    ...candidate,
    type: typeof type === 'string' && type.trim() !== '' ? type : 'unknown',
  };
}

/**
 * Keeps the rows a picker guarantees, and the page information with them.
 *
 * @remarks
 * The three pickers differ only in which rows they keep, so the shape they hand
 * over is built here rather than three times. Dropping the envelope would make
 * the notice unreachable: a picker cannot say "this is not all" once the only
 * thing that knew is gone.
 *
 * A row without a type is kept, with the literal `unknown`.
 *
 * @param answer - The envelope, already validated by the client.
 * @param keeps - Whether a row carries what this picker guarantees.
 */
export function selectedFrom<TItem>(
  answer: Answer,
  keeps: (row: unknown) => row is TItem,
): Selected<TItem> {
  const items = answer.data.map(fillMissingType).filter(keeps);
  return {
    droppedRowCount: answer.data.length - items.length,
    items,
    page: pageOf(answer),
  };
}
